/**
 * Query Configuration for React Query
 *
 * Centralized staleTime settings to optimize API calls and caching.
 * This prevents duplicate requests and improves frontend performance.
 *
 * @module useQueries
 * @description Provides consistent query configuration across the app.
 * All useQuery calls should use these settings for better caching.
 */

// Query configuration with staleTime in milliseconds
export const QUERY_CONFIG = {
  // User data - changes rarely, safe to cache 5 minutes
  currentUser: {
    staleTime: 5 * 60 * 1000,     // 5 minutes
    refetchOnWindowFocus: false
  },

  // Price data - updates every 5 minutes from cron
  prices: {
    staleTime: 5 * 60 * 1000,     // 5 minutes
    refetchOnWindowFocus: false
  },

  // Vaults - rarely change, 1 minute cache
  vaults: {
    staleTime: 60 * 1000,         // 1 minute
    refetchOnWindowFocus: false
  },

  // Investments - may have new earnings, 30 sec cache
  investments: {
    staleTime: 30 * 1000,         // 30 seconds
    refetchOnWindowFocus: true
  },

  // Referral data - 1 minute cache
  referral: {
    staleTime: 60 * 1000,         // 1 minute
    refetchOnWindowFocus: false
  },

  // Marketplace listings - 30 seconds cache
  marketplace: {
    staleTime: 30 * 1000,         // 30 seconds
    refetchOnWindowFocus: true
  },

  // Config - changes very rarely, 10 minutes cache
  config: {
    staleTime: 10 * 60 * 1000,    // 10 minutes
    refetchOnWindowFocus: false
  },

  // Balances - important to be fresh, 15 seconds
  balances: {
    staleTime: 15 * 1000,         // 15 seconds
    refetchOnWindowFocus: true
  },

  // TVL data - 1 minute cache
  tvl: {
    staleTime: 60 * 1000,         // 1 minute
    refetchOnWindowFocus: false
  }
} as const

/**
 * Helper to get query options with defaults
 */
export function getQueryOptions(key: keyof typeof QUERY_CONFIG) {
  return QUERY_CONFIG[key]
}

export default QUERY_CONFIG
